import { useCache, useSessionCache } from '~/common/useCache'

export const shuffleArray = <T>(array: T[]): T[] => {
  const shuffled = [...array]
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]]
  }
  return shuffled
}

// 영수증 열기
export const openReceipt = (url: string) => {
  const width = 400 // 원하는 창 너비
  const height = 700 // 원하는 창 높이

  // 창 옵션 설정
  const windowFeatures = `
    width=${width},
    height=${height},
    menubar=no,
    toolbar=no,
    location=no,
    status=no,
    scrollbars=yes,
    resizable=yes
  `.replace(/\s/g, '')

  // GA 이벤트: 영수증 팝업 열기 (범용)
  try {
    if (process.client) {
      const { trackEvent } = useAnalytics()
      const { user } = useAppStore()
      const router = useRouter()

      trackEvent('receipt_opened', {
        event_category: 'popup_interaction',
        receipt_url: url,
        popup_width: width,
        popup_height: height,
        page_path: router.currentRoute.value.path,
        um_idx: user?.UM_IDX ?? '',
        timestamp: new Date().toISOString(),
      })
    }
  } catch (error) {
    console.warn('GA tracking failed for receipt_opened:', error)
  }

  window.open(url, '영수증', windowFeatures)
}
export const logoutAndCleanData = () => {
  // 로그아웃시 캐시 데이터 삭제
  const app = useAppStore()
  const authStore = useAuthStore()

  // 로그아웃시 쿠키 삭제
  const cookie = useCookie('user')
  cookie.value = undefined

  document.cookie = 'user=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;'
  // 저장소 초기화
  localStorage.removeItem('auth') // 인증 정보
  localStorage.removeItem('jadoo') // 앱 상태 정보

  sessionStorage.clear()

  //로그아웃시 스토어 데이터 삭제
  app.$dispose()
  authStore.$dispose()

  useRouter().push('/login')

  //200 지연후 이동
  setTimeout(() => {
    location.reload()
  }, 200)
}

/**
 * JSON 객체의 키에서 특수문자를 제거하는 함수
 * 특수문자가 포함된 키를 발견하면 특수문자를 제거한 새로운 키를 생성하여 같은 값으로 객체에 추가
 * 중첩 객체와 배열 내부의 객체도 재귀적으로 처리
 *
 * @param obj - 처리할 JSON 객체
 * @param removeOriginalKey - 원본 키를 삭제할지 여부 (기본값: false)
 * @param specialChars - 제거할 특수문자 패턴 (기본값: 콜론과 기타 특수문자들)
 * @param visited - 순환 참조 방지를 위한 WeakSet (내부 사용)
 * @returns 특수문자가 제거된 키가 추가된 새로운 객체
 */
export const removeSpecialCharsFromKeys = (
  obj: Record<string, any>,
  removeOriginalKey: boolean = false,
  specialChars: RegExp = /[:!@#$%^&*()+=\[\]{}|\\;'"<>?/]/g,
  visited?: WeakSet<object>,
): Record<string, any> => {
  // 입력값 유효성 검사 - 객체가 아니거나 배열이면 빈 객체 반환
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) {
    return {}
  }

  // 순환 참조 방지를 위한 WeakSet 초기화
  const visitedSet = visited || new WeakSet()

  // 이미 처리된 객체인 경우 무한 루프 방지
  if (visitedSet.has(obj)) {
    return obj
  }

  // 현재 객체를 방문 목록에 추가
  visitedSet.add(obj)

  const result: Record<string, any> = { ...obj }

  // for...in 문을 사용하여 객체 순회 (요구사항)
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      const value = obj[key]
      let processedValue = value

      // 값이 객체인 경우 재귀적으로 처리
      if (value && typeof value === 'object' && !Array.isArray(value)) {
        processedValue = removeSpecialCharsFromKeys(
          value,
          removeOriginalKey,
          specialChars,
          visitedSet,
        )
      }
      // 값이 배열인 경우 배열 내부의 객체들을 처리
      else if (Array.isArray(value)) {
        processedValue = value.map((item) => {
          if (item && typeof item === 'object' && !Array.isArray(item)) {
            return removeSpecialCharsFromKeys(
              item,
              removeOriginalKey,
              specialChars,
              visitedSet,
            )
          }
          return item
        })
      }

      const cleanedKey = key.replace(specialChars, '')

      // 특수문자가 제거된 키가 원본 키와 다른 경우에만 처리
      if (cleanedKey !== key && cleanedKey.length > 0) {
        // 새로운 키로 처리된 값 추가
        result[cleanedKey] = processedValue

        // 원본 키 삭제 여부에 따른 처리
        if (removeOriginalKey) {
          delete result[key]
        } else {
          // 원본 키도 처리된 값으로 업데이트
          result[key] = processedValue
        }
      } else {
        // 키는 변경되지 않았지만 값은 처리된 값으로 업데이트
        result[key] = processedValue
      }
    }
  }

  return result
}
